import {StackScreenProps} from '@react-navigation/stack';
import {Box, Text, Toast} from 'native-base';
import React, {useCallback, useRef, useState} from 'react';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {QRScanner} from '../components/QRScanner';
import {useUserContext} from '../lib/authContext';
import {triggerHapticFeedback} from '../lib/hapticFeedback';
import i18n from '../lib/i18n';
import {formatIntAmount} from '../lib/formatters';
import {parsePaymentRequest} from '../lib/paymentRequest';
import {config} from '../stage.config';
import {Nav, NavParams} from './navigation';

const RETRY_DELAY = 2000;
const MAX_AMOUNT = config.production ? 1000 : 100000;

export const ScanPaymentScreen: React.FC<StackScreenProps<NavParams, Nav.ScanPaymentScreen>> = ({
  navigation
}) => {
  const {phoneNumber: ownPhoneNumber} = useUserContext();
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);

  const reject = (description: string) => {
    Toast.show({description, duration: RETRY_DELAY});
    setTimeout(() => {
      pausedRef.current = false;
      setPaused(false);
    }, RETRY_DELAY);
  };

  const handleScan = useCallback(
    (data: string) => {
      if (pausedRef.current) return;
      pausedRef.current = true;
      setPaused(true);

      let request;
      try {
        request = parsePaymentRequest(data);
      } catch (e) {
        request = null;
      }
      if (!request) return reject(i18n.t('invalidPaymentQR'));
      if (request.phoneNumber === ownPhoneNumber) return reject(i18n.t('cannotPayYourself'));
      if (request.amount && request.amount > MAX_AMOUNT) {
        return reject(i18n.t('maxAmountError', {amount: formatIntAmount(MAX_AMOUNT)}));
      }

      triggerHapticFeedback();
      const {phoneNumber, amount, message} = request;
      if (amount) {
        navigation.replace(Nav.CreatePaymentScreens, {
          screen: Nav.CreatePaymentMessageScreen,
          params: {phoneNumber, amount, message}
        });
        return;
      }
      navigation.replace(Nav.CreatePaymentScreens, {
        screen: Nav.CreatePaymentAmountScreen,
        params: {phoneNumber}
      });
    },
    [navigation, ownPhoneNumber]
  );

  return (
    <SafeAreaView style={[t.flex1, t.bgBlack]} edges={['bottom']}>
      <Box style={[t.flex1, t.relative]}>
        <QRScanner onScan={handleScan} paused={paused} />
        <Box style={[t.absolute, t.inset0, t.itemsCenter, t.justifyCenter]} pointerEvents="none">
          <Box style={[{width: 240, height: 240}, t.border4, t.borderWhite, t.roundedLg]} />
        </Box>
        <Box style={[t.absolute, t.insetX0, t.bottom0, t.p6]} pointerEvents="none">
          <Text color="white" textAlign="center">
            {i18n.t('scanPaymentHint')}
          </Text>
        </Box>
      </Box>
    </SafeAreaView>
  );
};
